import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import api from '../services/api';
import { useProject } from './ProjectContext';
import type { CoverageScore, TopicLabel } from '../types';

interface TopicWorkspaceContextType {
  // Re-export from ProjectContext for convenience.
  //
  // The category members are gone. They existed because the panes sat at the
  // bottom of a fixed project → category → topic walk and needed to know all
  // three. There is one node id now, and the last reader of the category — a
  // pair of links in TopicPagesPane pointing back into the drill-down — went
  // with that screen.
  selectedProjectId: number | null;
  selectedTopic: ReturnType<typeof useProject>['selectedTopic'];
  setSelectedTopicId: ReturnType<typeof useProject>['setSelectedTopicId'];

  /**
   * Whether a regroup + relink is in flight for the selected node.
   *
   * It lives HERE rather than in the pane that starts it, because only the
   * active tab is mounted: press the button on Plan, switch to Evidence, and
   * the pane holding that flag is gone. The run is not — it is a queued job
   * that will change what both tabs show when it lands, and nothing was
   * telling you it was still going.
   */
  regrouping: boolean;
  setRegrouping: (running: boolean) => void;

  // Context switcher modal
  isContextSwitcherOpen: boolean;
  openContextSwitcher: () => void;
  closeContextSwitcher: () => void;

  // Topic labels
  topicLabels: TopicLabel[];
  topicLabelsLoading: boolean;
  refreshTopicLabels: () => Promise<void>;

  // Coverage
  coverageScore: CoverageScore | null;
  coverageLoading: boolean;
  refreshCoverageScore: () => Promise<void>;

  // Shared threshold
  relevanceThreshold: number;
  setRelevanceThreshold: (t: number) => void;

  // Cross-pane refresh trigger
  linkedKeywordsVersion: number;
  triggerLinkedRefresh: () => void;
}

const TopicWorkspaceContext = createContext<TopicWorkspaceContextType | undefined>(
  undefined
);

interface TopicWorkspaceProviderProps {
  children: ReactNode;
}

export function TopicWorkspaceProvider({ children }: TopicWorkspaceProviderProps) {
  const [regrouping, setRegrouping] = useState(false);
  const {
    selectedProjectId,
    selectedTopic,
    selectedTopicId,
    setSelectedTopicId,
  } = useProject();

  // Context switcher modal
  const [isContextSwitcherOpen, setIsContextSwitcherOpen] = useState(false);

  // Topic labels
  const [topicLabels, setTopicLabels] = useState<TopicLabel[]>([]);
  const [topicLabelsLoading, setTopicLabelsLoading] = useState(false);

  // Coverage score
  const [coverageScore, setCoverageScore] = useState<CoverageScore | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // Relevance threshold
  const [relevanceThreshold, setRelevanceThreshold] = useState(0.8);

  // Cross-pane refresh
  const [linkedKeywordsVersion, setLinkedKeywordsVersion] = useState(0);

  // Context switcher
  const openContextSwitcher = useCallback(() => {
    setIsContextSwitcherOpen(true);
  }, []);

  const closeContextSwitcher = useCallback(() => {
    setIsContextSwitcherOpen(false);
  }, []);

  // Refresh topic labels
  const refreshTopicLabels = useCallback(async () => {
    if (!selectedTopicId) {
      setTopicLabels([]);
      return;
    }

    setTopicLabelsLoading(true);
    try {
      const response = await api.getTopicLabels(selectedTopicId);
      setTopicLabels(response.labels);
    } catch (err) {
      console.error('Failed to fetch topic labels:', err);
      setTopicLabels([]);
    } finally {
      setTopicLabelsLoading(false);
    }
  }, [selectedTopicId]);

  // Refresh coverage score
  const refreshCoverageScore = useCallback(async () => {
    if (!selectedTopicId) {
      setCoverageScore(null);
      return;
    }

    setCoverageLoading(true);
    try {
      const score = await api.getTopicCoverageScore(selectedTopicId, relevanceThreshold);
      setCoverageScore(score);
    } catch (err) {
      console.error('Failed to fetch coverage score:', err);
      setCoverageScore(null);
    } finally {
      setCoverageLoading(false);
    }
  }, [selectedTopicId, relevanceThreshold]);

  // Trigger linked keywords refresh
  const triggerLinkedRefresh = useCallback(() => {
    setLinkedKeywordsVersion((v) => v + 1);
  }, []);

  // Fetch topic labels when topic changes
  useEffect(() => {
    refreshTopicLabels();
  }, [refreshTopicLabels]);

  // Fetch coverage score when topic or threshold changes
  useEffect(() => {
    refreshCoverageScore();
  }, [refreshCoverageScore]);

  // Also refresh coverage when linkedKeywordsVersion changes
  useEffect(() => {
    if (linkedKeywordsVersion > 0) {
      refreshCoverageScore();
    }
  }, [linkedKeywordsVersion, refreshCoverageScore]);

  const value: TopicWorkspaceContextType = {
    selectedProjectId,
    selectedTopic,
    setSelectedTopicId,
    regrouping,
    setRegrouping,
    isContextSwitcherOpen,
    openContextSwitcher,
    closeContextSwitcher,
    topicLabels,
    topicLabelsLoading,
    refreshTopicLabels,
    coverageScore,
    coverageLoading,
    refreshCoverageScore,
    relevanceThreshold,
    setRelevanceThreshold,
    linkedKeywordsVersion,
    triggerLinkedRefresh,
  };

  return (
    <TopicWorkspaceContext.Provider value={value}>
      {children}
    </TopicWorkspaceContext.Provider>
  );
}

export function useTopicWorkspace() {
  const context = useContext(TopicWorkspaceContext);
  if (context === undefined) {
    throw new Error(
      'useTopicWorkspace must be used within a TopicWorkspaceProvider'
    );
  }
  return context;
}
